import React, { useCallback } from 'react';

import { Form, Input, Modal } from 'antd';
import { useIntl } from 'react-intl';

import { DELAY_REASON_MAX_LENGTH } from '@src/packages/workOrders/constants';

import type { DelayReasonModalProps } from './DelayReasonModal.types';

interface DelayReasonFormValues {
  reason: string;
}

/**
 * Asks for the delay justification before reporting it. The reason is mandatory: the
 * backend rejects a stall without one, and it is what the state history ends up showing.
 */
const DelayReasonModal = ({ open, loading, onOk, onCancel }: DelayReasonModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<DelayReasonFormValues>();

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onOk(values.reason.trim());
    } catch {
      // validateFields rejects with the failed fields; the Form already renders the messages.
    }
  }, [form, onOk]);

  return (
    <Modal
      destroyOnClose
      centered
      maskClosable={false}
      open={open}
      confirmLoading={loading}
      title={intl.formatMessage({ id: 'work.order.delays.report.title' })}
      okText={intl.formatMessage({ id: 'work.order.delays.report' })}
      cancelText={intl.formatMessage({ id: 'button.cancel' })}
      onOk={() => void handleOk()}
      onCancel={onCancel}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reason"
          label={intl.formatMessage({ id: 'work.order.delays.report.reason' })}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'work.order.delays.report.reason.required' }),
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={DELAY_REASON_MAX_LENGTH}
            showCount
            autoFocus
            placeholder={intl.formatMessage({ id: 'work.order.delays.report.reason' })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(DelayReasonModal);
